import axios from 'axios';
import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ClipboardCheck, CheckCircle2, XCircle, Plus, Trash2, Edit3,
  Save, Home, Sun, Moon, Truck, Camera, Calendar, User,
  ChevronDown, ChevronUp, GripVertical, AlertTriangle, Image,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const TYPE_LABELS = {
  interieur: { label: 'Intérieur', icon: Home, color: 'blue' },
  exterieur_ferme: { label: 'Extérieur Fermé', icon: Moon, color: 'purple' },
  exterieur_ouvert: { label: 'Extérieur Ouvert', icon: Sun, color: 'amber' },
  rangement_unites: { label: 'Rangement Unités', icon: Truck, color: 'green' },
};

// ─── Onglet Critères ──────────────────────────────────────────────────────────
function OngletCriteres() {
  const [criteres, setCriteres] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState('interieur');
  const [editingIdx, setEditingIdx] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCriteres();
  }, []);

  const fetchCriteres = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/inspections/criteres`);
      const data = res.data;
      setCriteres(data.criteres || {});
    } catch (e) {
      toast.error('Erreur chargement critères');
    } finally {
      setLoading(false);
    }
  };

  const saveCriteres = async (type, updatedList) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/inspections/criteres/${type}`, 
        { criteres: updatedList }
      );
      if (res.status !== 200) throw new Error('Erreur');
      const data = res.data;
      setCriteres(prev => ({ ...prev, [type]: data.criteres }));
      toast.success('Critères mis à jour!');
    } catch (e) {
      toast.error('Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSave = async (idx) => {
    const list = [...(criteres[activeType] || [])];
    list[idx] = { ...list[idx], label: editValue };
    setEditingIdx(null);
    await saveCriteres(activeType, list);
  };

  const handleDelete = async (idx) => {
    const list = (criteres[activeType] || []).filter((_, i) => i !== idx);
    await saveCriteres(activeType, list);
  };

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    const list = [...(criteres[activeType] || []), { label: newLabel.trim() }];
    setNewLabel('');
    await saveCriteres(activeType, list);
  };

  const currentList = criteres[activeType] || [];

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Modifiez les critères d'inspection pour chaque type de chantier. Ces critères sont utilisés dans les bons de travail.
      </p>

      {/* Type selector */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(TYPE_LABELS).map(([key, { label, icon: Icon, color }]) => (
          <button
            key={key}
            onClick={() => { setActiveType(key); setEditingIdx(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
              activeType === key
                ? `bg-${color}-600 text-white border-${color}-600`
                : `bg-white text-gray-700 border-gray-200 hover:border-${color}-300`
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            <Badge variant="secondary" className="ml-1 text-xs">{(criteres[key] || []).length}</Badge>
          </button>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" />
            {TYPE_LABELS[activeType]?.label} — {currentList.length} critères
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-gray-400 py-4">Chargement...</p>
          ) : (
            <div className="space-y-2">
              {currentList.map((c, idx) => (
                <div key={c.id || idx} className="flex items-center gap-2 p-2 rounded-lg border bg-gray-50 group">
                  <span className="text-gray-300 shrink-0">
                    <GripVertical className="w-4 h-4" />
                  </span>
                  <span className="text-xs text-gray-400 w-6 shrink-0 font-mono">{idx + 1}.</span>

                  {editingIdx === idx ? (
                    <div className="flex-1 flex gap-2">
                      <Input
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        className="flex-1 h-8 text-sm"
                        autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') handleEditSave(idx); if (e.key === 'Escape') setEditingIdx(null); }}
                      />
                      <Button size="sm" className="h-8 px-3" onClick={() => handleEditSave(idx)} disabled={saving}>
                        <Save className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditingIdx(null)}>✕</Button>
                    </div>
                  ) : (
                    <>
                      <span className="flex-1 text-sm text-gray-700">{c.label}</span>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-500"
                          onClick={() => { setEditingIdx(idx); setEditValue(c.label); }}
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400"
                          onClick={() => handleDelete(idx)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}

              {/* Add new criterion */}
              <div className="flex gap-2 pt-2 border-t mt-3">
                <Input
                  placeholder="Nouveau critère..."
                  value={newLabel}
                  onChange={e => setNewLabel(e.target.value)}
                  className="flex-1 text-sm"
                  onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
                />
                <Button onClick={handleAdd} disabled={!newLabel.trim() || saving} size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Formulaire Inspection Rangement ─────────────────────────────────────────
function FormInspectionRangement({ criteres, onSaved, onCancel, inspection = null }) {
  const { user } = useAuth();
  const [reponses, setReponses] = useState(inspection?.reponses || {});
  const [notes, setNotes] = useState(inspection?.notes || '');
  const [date, setDate] = useState(
    inspection?.date_inspection
      ? inspection.date_inspection.split('T')[0]
      : new Date().toISOString().split('T')[0]
  );
  const [photos, setPhotos] = useState(inspection?.photos || []);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [teamLeads, setTeamLeads] = useState([]);
  const [chefEquipeId, setChefEquipeId] = useState(inspection?.chef_equipe_id || '');
  const [chefEquipeNom, setChefEquipeNom] = useState(inspection?.chef_equipe_nom || '');
  const [unitesRangement, setUnitesRangement] = useState([]);
  const [uniteId, setUniteId] = useState(inspection?.unite_rangement_id || '');

  useEffect(() => {
    axios.get(`${API_URL}/api/team-leads`)
      .then(r => r.status === 200 ? r.data : { team_leads: [] })
      .then(d => {
        setTeamLeads(d.team_leads || []);
        // Cat H — pré-remplissage UI du form inspection selon rôle terrain de l'utilisateur (dispatch, pas gate RBAC).
        // Si l'utilisateur est un chef d'équipe, pré-remplir son équipe
        if (aLeRole(user, 'chef_equipe') && !inspection) {
          const moi = d.team_leads?.find(tl => tl.id === user.id);
          if (moi) {
            setChefEquipeId(moi.id);
            const nomChef = moi.name || `${moi.prenom || ''} ${moi.nom || ''}`.trim();
            setChefEquipeNom(nomChef);
            if (moi.unite_rangement_id) setUniteId(moi.unite_rangement_id);
          } else {
            // Fallback: pré-remplir depuis les données d'auth
            setChefEquipeId(user.id);
            setChefEquipeNom(`${user.prenom || ''} ${user.nom || ''}`.trim());
          }
        }
      })
      .catch(() => {});
    // Cat H — pré-remplissage UI (unité de rangement) selon rôle terrain de l'utilisateur, pas gate RBAC.
    // Récupérer l'unité depuis db.employees via employee_id (source principale)
    if (aLeRole(user, 'chef_equipe') && user?.employee_id && !inspection) {
      axios.get(`${API_URL}/api/employees/${user.employee_id}`)
        .then(r => r.status === 200 ? r.data : null)
        .then(d => {
          if (!d) return;
          const emp = d.employee || d;
          if (emp.unite_rangement_id) setUniteId(emp.unite_rangement_id);
        })
        .catch(() => {});
    }
    axios.get(`${API_URL}/api/unites-rangement`).then(r => r.data).then(d => setUnitesRangement(d.unites || [])).catch(() => {});
  }, []);

  const nbTotal = criteres.length;
  const nbReussi = criteres.filter(c => reponses[c.id] === true).length;
  const nbEchoue = criteres.filter(c => reponses[c.id] === false).length;
  const reussie = nbEchoue === 0 && nbTotal > 0;

  const toggleReponse = (id) => {
    setReponses(prev => {
      if (prev[id] === undefined) return { ...prev, [id]: true };
      if (prev[id] === true) return { ...prev, [id]: false };
      const copy = { ...prev }; delete copy[id]; return copy;
    });
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await axios.post(`${API_URL}/api/upload`);
        if (res.status === 200) {
          const data = res.data;
          setPhotos(prev => [...prev, data.url]);
        }
      } catch { toast.error('Erreur upload photo'); }
    }
  };

  const handleSave = async () => {
    if (!date) { toast.error('Date requise'); return; }
    if (!chefEquipeId) { toast.error('Veuillez sélectionner un chef d\'équipe'); return; }
    setSaving(true);
    try {
      const payload = {
        date_inspection: date,
        inspecteur_id: user?.id || '',
        inspecteur_nom: user ? `${user.prenom} ${user.nom}` : '',
        chef_equipe_id: chefEquipeId,
        chef_equipe_nom: chefEquipeNom,
        notes,
        photos,
        reponses,
      };
      const url = inspection
        ? `${API_URL}/api/inspections/rangement/${inspection.id}`
        : `${API_URL}/api/inspections/rangement`;
      const res = inspection
        ? await axios.put(url, payload)
        : await axios.post(url, payload);
      if (res.status !== 200) throw new Error('Erreur');
      toast.success(inspection ? 'Inspection mise à jour!' : 'Inspection créée!');
      onSaved();
    } catch (e) {
      toast.error('Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header info */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label>Date de l'inspection <span className="text-red-500">*</span></Label>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div>
          <Label>Chef d'équipe concerné <span className="text-red-500">*</span></Label>
          <select
            className="w-full border rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={chefEquipeId}
            onChange={e => {
              const selected = teamLeads.find(tl => tl.id === e.target.value);
              setChefEquipeId(e.target.value);
              setChefEquipeNom(selected?.name || '');
            }}
          >
            <option value="">— Sélectionner —</option>
            {teamLeads.map(tl => (
              <option key={tl.id} value={tl.id}>{tl.name}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>Inspecté par</Label>
          <Input
            value={user ? `${user.prenom} ${user.nom}` : ''}
            disabled
            className="bg-gray-50"
          />
        </div>
      </div>

      {/* Résultat global */}
      {Object.keys(reponses).length > 0 && (
        <div className={`flex items-center gap-3 p-3 rounded-lg border ${reussie ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          {reussie
            ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
            : <XCircle className="w-5 h-5 text-red-600 shrink-0" />
          }
          <span className="text-sm font-medium">
            {reussie ? 'Inspection réussie' : `${nbEchoue} critère(s) échoué(s) — Inspection non conforme`}
          </span>
          <span className="ml-auto text-xs text-gray-500">{nbReussi}/{nbTotal} réussis</span>
        </div>
      )}

      {/* Checklist */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Truck className="w-4 h-4 text-green-600" />
            Critères — Rangement des Unités ({nbTotal})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {criteres.map((c, idx) => {
              const val = reponses[c.id];
              return (
                <div
                  key={c.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border text-sm transition-colors cursor-pointer ${
                    val === true ? 'bg-green-50 border-green-200' :
                    val === false ? 'bg-red-50 border-red-200' :
                    'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => toggleReponse(c.id)}
                >
                  <button
                    type="button"
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                      val === true ? 'bg-green-500 border-green-500 text-white' :
                      val === false ? 'bg-red-500 border-red-500 text-white' :
                      'border-gray-300 bg-white'
                    }`}
                  >
                    {val === true && <CheckCircle2 className="w-3.5 h-3.5" />}
                    {val === false && <XCircle className="w-3.5 h-3.5" />}
                  </button>
                  <span className="text-xs text-gray-400 w-5 shrink-0 font-mono">{idx + 1}.</span>
                  <span className={val === false ? 'text-red-700' : 'text-gray-700'}>{c.label}</span>
                  <span className="ml-auto text-xs text-gray-400">
                    {val === undefined ? 'Non évalué' : val ? '✓ OK' : '✗ Échoué'}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Cliquez une fois : ✓ OK | Cliquez deux fois : ✗ Échoué | Cliquez trois fois : Effacer
          </p>
        </CardContent>
      </Card>

      {/* Notes */}
      <div>
        <Label>Notes</Label>
        <Textarea
          placeholder="Observations, remarques..."
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
        />
      </div>

      {/* Photos */}
      <div>
        <Label>Photos</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {photos.map((url, i) => (
            <div key={url.id || url._id || i} className="relative w-20 h-20 rounded-lg overflow-hidden border bg-gray-100">
              <img src={url} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))}
              >✕</button>
            </div>
          ))}
          <button
            type="button"
            className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-gray-400 transition-colors"
            onClick={() => fileRef.current?.click()}
          >
            <Camera className="w-5 h-5" />
            <span className="text-xs mt-1">Photo</span>
          </button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel}>Annuler</Button>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Sauvegarde...' : (inspection ? 'Mettre à jour' : 'Créer l\'inspection')}
        </Button>
      </div>
    </div>
  );
}

// ─── Onglet Rangement Unités ──────────────────────────────────────────────────
function OngletRangement() {
  const [inspections, setInspections] = useState([]);
  const [criteres, setCriteres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editInspection, setEditInspection] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [inspRes, critRes] = await Promise.all([
        axios.get(`${API_URL}/api/inspections/rangement`),
        axios.get(`${API_URL}/api/inspections/criteres/rangement_unites`),
      ]);
      if (inspRes.status === 200) {
        const d = inspRes.data;
        setInspections(d.inspections || []);
      }
      if (critRes.status === 200) {
        const d = critRes.data;
        setCriteres(d.criteres || []);
      }
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette inspection?')) return;
    await axios.delete(`${API_URL}/api/inspections/rangement/${id}`);
    toast.success('Inspection supprimée');
    fetchAll();
  };

  if (showForm || editInspection) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Truck className="w-4 h-4 text-green-600" />
            {editInspection ? 'Modifier l\'inspection' : 'Nouvelle inspection de rangement'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FormInspectionRangement
            criteres={criteres}
            inspection={editInspection}
            onSaved={() => { setShowForm(false); setEditInspection(null); fetchAll(); }}
            onCancel={() => { setShowForm(false); setEditInspection(null); }}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          Les inspections de rangement d'unités sont indépendantes des bons de travail et influencent le critère de performance <strong>Propreté des unités</strong>.
        </p>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle inspection
        </Button>
      </div>

      {loading ? (
        <p className="text-center py-8 text-gray-400">Chargement...</p>
      ) : inspections.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-400">
            <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucune inspection de rangement pour l'instant.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {inspections.map(insp => (
            <Card key={insp.id} className={`border-l-4 ${insp.reussie ? 'border-l-green-500' : 'border-l-red-500'}`}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {insp.reussie
                      ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                      : <XCircle className="w-5 h-5 text-red-600 shrink-0" />
                    }
                    <div>
                      <p className="font-medium text-sm">
                        Inspection du {new Date(insp.date_inspection + 'T12:00:00').toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-1 flex-wrap">
                        <User className="w-3 h-3" /> {insp.inspecteur_nom || 'N/A'}
                        {insp.chef_equipe_nom && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span className="font-medium text-gray-700">Chef : {insp.chef_equipe_nom}</span>
                          </>
                        )}
                        <span className="text-gray-300">·</span>
                        {insp.nb_reussi}/{insp.nb_total} critères
                        {insp.nb_echoue > 0 && <span className="text-red-500"> · {insp.nb_echoue} échoué(s)</span>}
                      </p>
                    </div>
                    <Badge className={insp.reussie ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {insp.reussie ? 'Conforme' : 'Non conforme'}
                    </Badge>
                    {insp.chef_equipe_nom && (
                      <Badge variant="outline" className="text-blue-700 border-blue-300">
                        {insp.chef_equipe_nom}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setExpandedId(expandedId === insp.id ? null : insp.id)}>
                      {expandedId === insp.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" className="text-blue-500" onClick={() => setEditInspection(insp)}>
                      <Edit3 className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDelete(insp.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Expanded details */}
                {expandedId === insp.id && (
                  <div className="mt-3 pt-3 border-t space-y-3">
                    {/* Checklist results */}
                    <div className="space-y-1">
                      {criteres.map(c => {
                        const val = insp.reponses?.[c.id];
                        if (val === undefined) return null;
                        return (
                          <div key={c.id} className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${val ? 'text-green-700' : 'text-red-700'}`}>
                            {val ? <CheckCircle2 className="w-3 h-3 shrink-0" /> : <XCircle className="w-3 h-3 shrink-0" />}
                            {c.label}
                          </div>
                        );
                      })}
                    </div>
                    {insp.notes && (
                      <p className="text-xs text-gray-600 italic bg-gray-50 rounded px-3 py-2">
                        {insp.notes}
                      </p>
                    )}
                    {insp.photos?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {insp.photos.map((url, i) => (
                          <img key={url.id || url._id || i} src={url} alt="" className="w-16 h-16 object-cover rounded border" />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Onglet Historique : uniquement les bons de travail (le rangement est dans Unités)
function OngletHistorique() {
  const [inspectionsBons, setInspectionsBons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const bonsRes = await axios.get(`${API_URL}/api/inspections/historique-bons`);
        setInspectionsBons(bonsRes.data?.inspections || []);
      } catch (e) {
        toast.error('Erreur chargement historique');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const TYPE_INSPECTION_LABELS = {
    interieur: 'Intérieur',
    exterieur_ferme: 'Extérieur Fermé',
    exterieur_ouvert: 'Extérieur Ouvert',
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Historique des inspections effectuées sur les bons de travail.
        Pour les inspections de rangement, consultez la page <strong>Unités</strong>.
      </p>
      {loading ? <p className="text-center py-6 text-gray-400">Chargement...</p>
      : inspectionsBons.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-gray-400">
          <ClipboardCheck className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>Aucune inspection enregistrée.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {inspectionsBons.map(insp => (
            <Card key={insp.id} className={`border-l-4 ${insp.reussie ? 'border-l-green-500' : 'border-l-red-500'}`}>
              <CardContent className="pt-3 pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    {insp.reussie
                      ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                      : <XCircle className="w-5 h-5 text-red-600 shrink-0" />}
                    <div>
                      <p className="font-medium text-sm">
                        Bon de travail — {TYPE_INSPECTION_LABELS[insp.type_inspection] || insp.type_travaux || 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {new Date(insp.date_inspection).toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' })}
                        &nbsp;·&nbsp;
                        <User className="w-3 h-3 inline mr-1" />
                        {insp.inspecteur_nom || insp.inspected_by || 'N/A'}
                        &nbsp;·&nbsp;{insp.nb_reussi || 0}/{insp.nb_total || 0}
                        {insp.nb_echoue > 0 && <span className="text-red-500"> · {insp.nb_echoue} échoué(s)</span>}
                      </p>
                    </div>
                  </div>
                  <Badge className={insp.reussie ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {insp.reussie ? 'Réussie' : 'Échouée'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export function Inspection() {
  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
          <ClipboardCheck className="w-7 h-7 text-purple-600" />
          Inspections
        </h1>
        <p className="text-gray-500 mt-1 text-sm">
          Gérez les critères d'inspection des bons de travail et consultez leur historique. Les inspections de rangement sont gérées dans la page <strong>Unités</strong>.
        </p>
      </div>

      <Tabs defaultValue="historique">
        <div className="overflow-x-auto -mx-1">
          <TabsList className="grid grid-cols-2 w-full max-w-md min-w-[240px] mx-1">
            <TabsTrigger value="historique" className="flex items-center gap-1 text-xs sm:text-sm">
              <ClipboardCheck className="w-3.5 h-3.5" />
              Historique
            </TabsTrigger>
            <TabsTrigger value="criteres" className="flex items-center gap-1 text-xs sm:text-sm">
              <Edit3 className="w-3.5 h-3.5" />
              Critères
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="historique" className="mt-4">
          <OngletHistorique />
        </TabsContent>
        <TabsContent value="criteres" className="mt-4">
          <OngletCriteres />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Inspection;
